// The business's official clock, for the web app.
//
// "Today" and "now" are the business's, not the browser's: a manager in
// another timezone, or a public booking page opened abroad, must still see the
// business's calendar. The timezone comes from /me (signed in) or from the
// public API responses (location.timezone). Until one is known — an older
// backend, or before /me has loaded — the browser's own zone is used, which is
// exactly how every screen behaved before.
//
// Dates are passed around as 'YYYY-MM-DD' strings. Where a component needs a
// Date object (pickers, calendars), use parseYmd(): it builds a local-midnight
// Date, never a UTC one, so the day never slips.

const browserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

let businessTimezone = null;

function isValidTimezone(tz) {
  if (!tz || typeof tz !== 'string') return false;
  try {
    Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** Set the business timezone (IANA name). Blank or invalid is ignored. */
export function setBusinessTimezone(tz) {
  businessTimezone = isValidTimezone(tz) ? tz : null;
}

/** The business timezone, or the browser's when none is known yet. */
export function getBusinessTimezone() {
  return businessTimezone || browserTimezone();
}

/** Wall-clock parts of a moment in a timezone. */
function partsIn(date, tz) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour: Number(get('hour')),
    minute: Number(get('minute')),
    second: Number(get('second'))
  };
}

const pad = (n) => String(n).padStart(2, '0');

/** Today on the business calendar, 'YYYY-MM-DD'. */
export function businessToday(tz = getBusinessTimezone()) {
  const p = partsIn(new Date(), tz);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

/**
 * "Now" on the business wall clock, as a local Date with the same wall-clock
 * fields (year…second). Compare it with other wall-clock Dates built locally,
 * e.g. new Date(y, m - 1, d, hh, mm) or parseYmd().
 */
export function businessNow(tz = getBusinessTimezone()) {
  const p = partsIn(new Date(), tz);
  return new Date(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
}

/** Today on the business calendar as a local-midnight Date. */
export function businessTodayDate(tz = getBusinessTimezone()) {
  return parseYmd(businessToday(tz));
}

/** 'YYYY-MM-DD' (or 'YYYY-MM-DD…') → local-midnight Date. Never UTC. */
export function parseYmd(ymd) {
  if (!ymd) return null;
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/** A local Date → 'YYYY-MM-DD' from its own (local) fields. */
export function toYmd(date) {
  if (!(date instanceof Date) || isNaN(date)) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Add whole days to a 'YYYY-MM-DD' date. */
export function addDaysYmd(ymd, days) {
  const d = parseYmd(ymd);
  if (!d) return ymd;
  d.setDate(d.getDate() + days);
  return toYmd(d);
}

/**
 * A recorded moment (ISO with an offset or 'Z') as a local Date whose
 * wall-clock fields are business time — for code that reads getHours() etc.
 */
export function inBusinessTz(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date)) return null;
  const p = partsIn(date, getBusinessTimezone());
  return new Date(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
}

/**
 * A business wall-clock value sent as text ('YYYY-MM-DD HH:mm[:ss]' or
 * 'YYYY-MM-DDTHH:mm[:ss]…', any offset ignored) as a local Date with those
 * same fields. Session and booking times are wall-clock values: their text is
 * the business time, whatever offset the server attached.
 */
export function parseWallClock(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(String(value || ''));
  if (!m) return null;
  const [, y, mo, d, h, mi, s] = m.map(Number);
  return new Date(y, mo - 1, d, h, mi, s || 0);
}

/** A date string: 'YYYY-MM-DD' as a local date (no UTC slip), anything else as given. */
export function parseDateLoose(value) {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return parseYmd(value);
  const date = new Date(value);
  return isNaN(date) ? null : date;
}

/**
 * Format a recorded moment (ISO with an offset or 'Z') in business time.
 * `options` are Intl.DateTimeFormat options; the timeZone is filled in.
 */
export function formatInBusinessTz(value, options = {}, locale = 'en-GB') {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date)) return '';
  return date.toLocaleString(locale, { ...options, timeZone: getBusinessTimezone() });
}
